import type {
  AdminSessionResponse,
  ApiErrorPayload,
} from "@contracts/admin";

let csrfToken: string | null = null;

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly details?: ApiErrorPayload["error"]["details"],
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export function setCsrfToken(value: string | null): void {
  csrfToken = value;
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const method = (init.method ?? "GET").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method) && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }

  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers,
  });

  if (!response.ok) {
    let payload: ApiErrorPayload | null = null;
    try {
      payload = (await response.json()) as ApiErrorPayload;
    } catch {
      // A mensagem abaixo cobre respostas não JSON sem vazar o corpo inesperado.
    }
    throw new ApiClientError(
      response.status,
      payload?.error.code ?? "request_failed",
      payload?.error.message ?? "Não foi possível concluir a requisição.",
      payload?.error.request_id,
      payload?.error.details,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export async function loadSession(): Promise<AdminSessionResponse | null> {
  try {
    const session = await apiRequest<AdminSessionResponse>("/api/v1/auth/session");
    setCsrfToken(session.csrf_token);
    return session;
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw error;
  }
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}
